/** @jsx jsx */
import { jsx } from '@emotion/core'
import React, { Fragment, useEffect } from 'react'
import { Modal } from 'antd-mobile'
import { observer } from 'mobx-react'
import { isMobile } from 'react-device-detect'
import useRootStore from '@store/useRootStore'
import { message, Select } from 'antd'
import NumberFormat, { NumberFormatValues } from 'react-number-format'
import * as _ from 'lodash'
import { getErrorText } from '@game/errtext'
import * as Utils from '@utils/utils'
// icon
import { CircleDownIco } from '@shared/Icon/ArrowIcon'
// css
import { DropdownWrap, ModalBg } from '@styles/base.style'
import { Button, CloseBtn } from '@styles/button.style'
import { AdvSlideUpModalStyle } from '@styles/modal'
import { GmoneyIco } from '@shared/Icon/MoneyIcon'
import { IAuthenticationResult } from '@protocol/auth'
import { SettingLimitWrap } from './index.style'

const MIN_VALUE = 1000000

const SettingLimitPopup: React.FC = () => {
  const { globalStore, componentStore, authStore, joinStore } = useRootStore()
  const { visibleSettingLimit, setVisibleSettingLimit } = globalStore
  const { userLossMoney = 0, originalLossLimitAmount = null } = componentStore
  const maxBetLimit = globalStore.getClubGmoneyMaxBetLimit()
  const MAX_VALUE = _.toNumber(maxBetLimit) * 10

  useEffect(() => {
    if (visibleSettingLimit) {
      componentStore.setOriginalLossLimitAmount(null)
      componentStore.setLossLimitAmount(null)
      componentStore.setUserLossMoney(0)
      componentStore.setSelfUsageRestrictionMonthPeriod(null)
      componentStore.fetchLossLimitAmountAndUserLossMoney()
    }
  }, [visibleSettingLimit])

  const onAuthenticationResultHandler = async (result: IAuthenticationResult.Schema) => {
    if (!authStore.validateAuthenticationResult(result) || authStore.certificationInfo?.ci !== result.conninfo) {
      Utils.showToast(Utils.TOAST_TYPE.WARN, '본인 인증에 실패했습니다. 다시 시도해 주세요.')
      return
    }

    await saveLossLimit(false)
    await saveSelfUsageRestriction()
    globalStore.setVisibleSelectCertification(false)
  }

  const onCloseSettingLimitModal = () => {
    setVisibleSettingLimit(false)
  }

  const validateLimitValue = (isVisibleResultMessage) => {
    const { lossLimitAmount } = componentStore

    let result = true
    let messageText = ''

    if (result && !_.isNumber(lossLimitAmount)) {
      messageText = '손실한도를 입력해주세요.'
      result = false
    }

    if (result && (lossLimitAmount === originalLossLimitAmount)) {
      messageText = '변경사항이 없습니다.'
      result = false
    }

    if (result && (lossLimitAmount < MIN_VALUE || lossLimitAmount > MAX_VALUE)) {
      messageText = `손실한도는 ${MIN_VALUE.toLocaleString()}G ~ ${MAX_VALUE.toLocaleString()}G까지 입력 가능합니다.`
      result = false
    }

    if (!result && isVisibleResultMessage) {
      message.info(messageText)
    }

    return result
  }


  const onChangeSelfUsageRestriction = value => {
    componentStore.setSelfUsageRestrictionMonthPeriod(value)
  }

  const saveLossLimit = async (isVisibleResultMessage = true) => {
    if (validateLimitValue(isVisibleResultMessage)) {
      const result = await componentStore.saveLossLimitAmount()
      if (isVisibleResultMessage) {
        const { errcode } = result
        if (_.isNumber(errcode)) {
          message.error(getErrorText(errcode))
        } else {
          message.info('손실한도 변경을 완료했습니다.')
        }
      }
    }
  }

  const saveSelfUsageRestriction = async () => {
    const result = await componentStore.saveSelfUsageRestriction()
    const { errcode } = result
    if (_.isNumber(errcode)) {
      message.error(getErrorText(errcode))
    }
  }

  const onClickSettingLimit = async () => {
    const { selfUsageRestrictionMonthPeriod } = componentStore
    if (_.isNil(selfUsageRestrictionMonthPeriod)) {
      await saveLossLimit()
      onCloseSettingLimitModal()
    } else if (Utils.isNotSupportedBrowser()) {
      joinStore.showNotSupportedBrowserDialog()
    } else {
      authStore.setAdultAuthenticationResultHandler(onAuthenticationResultHandler)
      globalStore.setVisibleSelectCertification(true)
    }
  }

  const onPriceValueChange = async (values: NumberFormatValues) => {
    const { value } = values
    if (_.isEmpty(value)) {
      componentStore.setLossLimitAmount(null)
    } else {
      componentStore.setLossLimitAmount(_.toNumber(value))
    }
  }

  const { Option } = Select

  const CircleDown = (
    <span className="circle-down-ico">
    <CircleDownIco/>
  </span>
  )

  const { lossLimitAmount } = componentStore

  return (
    <Modal
      popup
      visible={visibleSettingLimit}
      maskClosable={false}
      animated={isMobile}
      animationType="slide-up"
      onClose={onCloseSettingLimitModal}
      className="full-modal message"
      css={AdvSlideUpModalStyle}
      title={
        <Fragment>
          <h1 className="title">이용 설정</h1>
          <Button onClick={onCloseSettingLimitModal} className="close-btn" css={CloseBtn}>
            <span className="close-txt">닫기</span>
          </Button>
        </Fragment>
      }
    >
      <SettingLimitWrap className={!isMobile && 'pc-setting-limit'}>
        <div className="loss-money">
          <ul>
            <li className="limit-money">
              <span className="limit-txt">1일 손실머니</span>
              <span className="money-area">
              {userLossMoney.toLocaleString()}
                <GmoneyIco/>
            </span>
            </li>
            <li className="limit-money setting-limit">
              <span className="limit-txt">손실한도</span>
              <span className="money-area">
              <NumberFormat
                pattern="\d*"
                decimalScale={0}
                maxLength={8}
                placeholder="-"
                allowNegative={false}
                value={lossLimitAmount}
                thousandSeparator
                onValueChange={onPriceValueChange}
              />
              <GmoneyIco/>
            </span>
            </li>
          </ul>
          <div className="explain-box">
            <h4 className="explain-tit">&#91;  1일 손실한도 &#93;</h4>
            <p className="explain-txt">
              설정한 1일 손실한도 초과 시, 6시간 동안 게임 이용이 제한됩니다.
            </p>
            <p className="point-color--red--modal">
              &#9312; 손실한도를 한번 설정하면, <span className="point-color--red--modal">해제가 불가</span>합니다.
            </p>
            <p className="explain-txt">
              &#9313; 한도 금액 수정은 주 1회 가능합니다. (매주 월요일)
            </p>
            <p className="explain-txt">
              &#9314; 1,000,000G ~ {MAX_VALUE.toLocaleString()}G에서만 손실 한도액 설정이 가능하며, 내일 0시부터 적용됩니다.
            </p>
          </div>

        </div>

        <div className="setting-term">
          <div className="limit-setting">
            <span className="limit-txt">이용 제한 설정</span>
            <DropdownWrap id="settingPopup" className="limit setting-popup">
              <Select
                onChange={onChangeSelfUsageRestriction}
                placeholder="-"
                getPopupContainer={() => document.getElementById('settingPopup')}
              >
                <Option value="1">1개월</Option>
                <Option value="3">3개월</Option>
                <Option value="6">6개월</Option>
                <Option value="12">12개월</Option>
              </Select>
              {CircleDown}
            </DropdownWrap>
          </div>

          <div className="explain-box">
            <h4 className="explain-tit">&#91; 이용 제한 &#93;</h4>
            <p className="explain-txt">
              설정한 기간만큼 게임 이용이 제한(<span className="point-color--red--modal">접속불가</span>)됩니다.
            </p>
            <p className="point-color--red--modal">
              &#9312; 이용제한을 한번 설정하면, <span className="point-color--red--modal">해제가 불가</span>합니다.
            </p>
            <p className="explain-txt">
              &#9313; 설정 즉시, 이용제한(<span className="point-color--red--modal">접속불가</span>)이 적용됩니다.
            </p>
          </div>
        </div>

        <Button navy onClick={onClickSettingLimit} className="change-btn">
          설정
        </Button>
      </SettingLimitWrap>

      {!isMobile && <ModalBg onClick={onCloseSettingLimitModal}/>}
    </Modal>
  )
}
export default observer(SettingLimitPopup)
